/**
 * Security Settings Step Component
 * TASK-339: Onboarding Wizard UI
 *
 * Step 3: Configure security policies (optional)
 */

import React, { useState } from 'react';
import { Loader2, Shield, ShieldCheck, Settings, Info } from 'lucide-react';
import { toast } from 'sonner';
import { adminOnboardingService } from '../../../services/adminOnboardingService';
import { SecurityPreset } from '../../../types/adminOnboarding';

interface SecurityStepProps {
  token: string;
  onComplete: () => void;
  onSkip: () => void;
}

const SecurityStep: React.FC<SecurityStepProps> = ({
  token,
  onComplete,
  onSkip,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<SecurityPreset>('standard');
  const [showCustom, setShowCustom] = useState(false);

  // Custom settings
  const [customSettings, setCustomSettings] = useState({
    min_password_length: 12,
    require_2fa_for_admin: false,
    require_2fa_for_all: false,
    session_timeout_minutes: 480,
    max_failed_attempts: 5,
  });

  const presets = [
    {
      id: 'standard' as SecurityPreset,
      name: 'Standard',
      icon: Shield,
      description: 'Swiss banking security defaults. Recommended for most firms.',
      features: [
        '12+ character passwords',
        'Uppercase, lowercase, numbers, special chars',
        '8-hour session timeout',
        '5 failed login attempts before lockout',
        'Optional 2FA',
      ],
    },
    {
      id: 'strict' as SecurityPreset,
      name: 'Strict',
      icon: ShieldCheck,
      description: 'Enhanced security for high-risk environments.',
      features: [
        '16+ character passwords',
        'Mandatory 2FA for admins',
        '4-hour session timeout',
        '3 failed login attempts before lockout',
        '60-minute lockout duration',
      ],
    },
    {
      id: 'custom' as SecurityPreset,
      name: 'Custom',
      icon: Settings,
      description: 'Configure your own security policies.',
      features: [
        'Set your own password length',
        'Choose 2FA requirements',
        'Custom session timeouts',
        'Adjustable lockout policy',
      ],
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await adminOnboardingService.saveSecuritySettings({
        token,
        preset: selectedPreset,
        ...(selectedPreset === 'custom' ? customSettings : {}),
      });

      if (response.success) {
        toast.success('Security settings configured!');
        onComplete();
      } else {
        toast.error('Failed to save security settings');
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || 'Failed to save security settings';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex gap-3">
        <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Swiss Banking Security Standards</p>
          <p className="mt-1 text-blue-700">
            All presets meet FINMA requirements for financial intermediaries.
            You can adjust these settings later in the Admin Panel.
          </p>
        </div>
      </div>

      {/* Preset selection */}
      <div className="space-y-3">
        {presets.map(preset => {
          const Icon = preset.icon;
          const isSelected = selectedPreset === preset.id;

          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => {
                setSelectedPreset(preset.id);
                setShowCustom(preset.id === 'custom');
              }}
              className={`
                w-full text-left p-4 rounded-lg border-2 transition-all
                ${isSelected
                  ? 'border-kielis-navy bg-kielis-navy/5'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
                }
              `}
            >
              <div className="flex items-start gap-4">
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center
                  ${isSelected ? 'bg-kielis-navy text-white' : 'bg-gray-100 text-gray-500'}
                `}>
                  <Icon size={20} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h3 className={`font-semibold ${isSelected ? 'text-kielis-navy' : 'text-gray-900'}`}>
                      {preset.name}
                    </h3>
                    {isSelected && (
                      <span className="text-xs font-medium text-kielis-navy bg-kielis-navy/10 px-2 py-1 rounded">
                        Selected
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{preset.description}</p>
                  <ul className="mt-3 space-y-1">
                    {preset.features.map((feature, idx) => (
                      <li key={idx} className="text-xs text-gray-500 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-gray-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Custom settings */}
      {showCustom && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          <h4 className="font-medium text-gray-900">Custom Configuration</h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Minimum Password Length
              </label>
              <select
                value={customSettings.min_password_length}
                onChange={(e) => setCustomSettings(prev => ({
                  ...prev,
                  min_password_length: parseInt(e.target.value),
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              >
                {[8, 10, 12, 14, 16, 20].map(len => (
                  <option key={len} value={len}>{len} characters</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Timeout
              </label>
              <select
                value={customSettings.session_timeout_minutes}
                onChange={(e) => setCustomSettings(prev => ({
                  ...prev,
                  session_timeout_minutes: parseInt(e.target.value),
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              >
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={240}>4 hours</option>
                <option value={480}>8 hours</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Failed Logins
              </label>
              <select
                value={customSettings.max_failed_attempts}
                onChange={(e) => setCustomSettings(prev => ({
                  ...prev,
                  max_failed_attempts: parseInt(e.target.value),
                }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-kielis-gold focus:border-transparent"
              >
                {[3, 5, 7, 10].map(num => (
                  <option key={num} value={num}>{num} attempts</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={customSettings.require_2fa_for_admin}
                onChange={(e) => setCustomSettings(prev => ({
                  ...prev,
                  require_2fa_for_admin: e.target.checked,
                }))}
                className="w-4 h-4 text-kielis-navy border-gray-300 rounded focus:ring-kielis-gold"
              />
              <span className="text-sm text-gray-700">Require 2FA for admin users</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={customSettings.require_2fa_for_all}
                onChange={(e) => setCustomSettings(prev => ({
                  ...prev,
                  require_2fa_for_all: e.target.checked,
                }))}
                className="w-4 h-4 text-kielis-navy border-gray-300 rounded focus:ring-kielis-gold"
              />
              <span className="text-sm text-gray-700">Require 2FA for all users</span>
            </label>
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="button"
          onClick={onSkip}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Skip
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 py-3 px-4 bg-kielis-navy text-white font-medium rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </form>
  );
};

export default SecurityStep;
